import React from "react";
import { Column } from "@/components/Datatable/types";
import { EmailNotification } from "@/api/getEmailNotifications/types";
import { CheckCircle, XCircle } from "lucide-react";
import EmailNotificationActionMenu from "@/features/adminEmailNotifications/components/EmailNotificationActionMenu/EmailNotificationActionMenu";

/**
 * Helper to format date/time into local user's system time zone,
 * just like your event session approach.
 */
function formatDateTime(dateString?: string | null): string {
  if (!dateString) return "--";
  const dateObj = new Date(dateString);
  if (isNaN(dateObj.getTime())) return "--"; // invalid date

  // Format with options to exclude seconds
  return dateObj.toLocaleString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/**
 * A reusable status badge component for boolean values
 */
interface StatusBadgeProps {
  value: boolean;
  positiveText?: string;
  negativeText?: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({
  value,
  positiveText = "Yes",
  negativeText = "No",
}) => {
  if (value) {
    return (
      <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle className="w-3.5 h-3.5 mr-1" />
        {positiveText}
      </div>
    );
  }

  return (
    <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
      <XCircle className="w-3.5 h-3.5 mr-1" />
      {negativeText}
    </div>
  );
};

/**
 * Props for the columns generator.
 *  - onAskApprove: callback to show the approve modal
 *  - onAskPreview: callback to show preview modal
 */
interface CreateEmailNotificationsColumnsProps {
  onAskApprove: (notification: EmailNotification) => void;
  onAskPreview: (notification: EmailNotification) => void;
}

/**
 * We build the columns, pulling in user info and adding a
 * dropdown-based Actions column that has "Approve" (if not approved) and "Preview."
 */
export function createEmailNotificationsColumns(
  props: CreateEmailNotificationsColumnsProps,
): Column<EmailNotification>[] {
  return [
    {
      header: "ID",
      accessorKey: "id",
    },
    {
      header: "Subject",
      accessorKey: "subject",
    },
    {
      header: "First Name",
      accessorKey: "userFirstName",
      cell: ({ row }) => row.original.userFirstName || "--",
    },
    {
      header: "Last Name",
      accessorKey: "userLastName",
      cell: ({ row }) => row.original.userLastName || "--",
    },
    {
      header: "Email",
      accessorKey: "userEmail",
      cell: ({ row }) => row.original.userEmail || "--",
    },
    {
      header: "Chapter",
      accessorKey: "chapterPermanentName",
      cell: ({ row }) => row.original.chapterPermanentName || "--",
    },
    {
      header: "Lead Status",
      accessorKey: "hubspotLeadStatus",
      cell: ({ row }) => row.original.hubspotLeadStatus || "--",
    },
    {
      header: "Approved",
      accessorKey: "approved",
      cell: ({ row }) => <StatusBadge value={Boolean(row.original.approved)} />,
    },
    {
      header: "Sent",
      accessorKey: "sent",
      cell: ({ row }) => <StatusBadge value={Boolean(row.original.sent)} />,
    },
    {
      header: "Created At",
      accessorKey: "createdAt",
      cell: ({ row }) => formatDateTime(row.original.createdAt),
    },
    {
      header: "Actions",
      id: "actions",
      cell: ({ row }) => {
        const notification = row.original;
        return (
          <EmailNotificationActionMenu
            notification={notification}
            onApprove={props.onAskApprove}
            onPreview={props.onAskPreview}
          />
        );
      },
    },
  ];
}
